import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { SearchSelect, type SearchSelectOption } from '@/components/ui/search-select';
import { RemoteSearchSelect } from '@/components/ui/remote-search-select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { GraduationCap, Info, BookOpen, Loader2, Sparkles } from 'lucide-react';
import { cycleForNiveau, normalizeLabel } from '../enMapping';
import {
  searchEnMefs,
  matieresForMef as fetchMatieresForMef,
  premapNiveauxAi,
  premapMatieresAi,
} from '../api';
import type {
  EnMappingDecisions,
  EnMappingModel,
  EnMappingSource,
  EnMatiereOption,
} from '../types';

const NONE = '__none__';

/** Libellés conviviaux des modalités d'élection (fallback : libellé api data). */
const MODALITE_LABELS: Record<string, string> = {
  O: 'Obligatoire',
  F: 'Facultative',
  S: 'Tronc commun',
  X: 'Spécifique',
  N: 'Oblig. ou fac.',
  D: 'DNL',
  R: 'Religieux',
  L: 'Ajout acad.',
  '9': '—',
};

function modaliteText(o: EnMatiereOption): string {
  if (o.modaliteCode && MODALITE_LABELS[o.modaliteCode]) return MODALITE_LABELS[o.modaliteCode];
  return o.modalite ?? '—';
}

/** Libellés du type de langue exigé (règles EN option-rules). */
const LANGUE_TYPE_LABELS: Record<string, string> = { LVE: 'étrangère', LVR: 'régionale' };

/**
 * Étiquette courte « langue vivante » dérivée des règles EN : rang (LV1..LV9) +
 * type de langue exigé (« étrangère » / « régionale »). Null si non-LV.
 * Ex. « LV1 · étrangère », « LV7 · régionale », « LV2 ».
 */
function lvLabel(o: EnMatiereOption): string | null {
  if (!o.isLangueVivante) return null;
  const rang = o.rang ? `LV${o.rang}` : 'LV';
  const types = (o.typeLangueRequis ?? []).map((t) => LANGUE_TYPE_LABELS[t] ?? t);
  return types.length > 0 ? `${rang} · ${types.join('/')}` : rang;
}

/** Marqueur ✨ « pré-rempli par l'IA » avec sa confiance. */
function AiMark({ confidence }: { confidence?: number | null }) {
  const pct = confidence != null ? Math.round(confidence * 100) : null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="ml-1 inline-flex align-middle">
          <Sparkles className="h-3.5 w-3.5 text-violet-500" />
        </span>
      </TooltipTrigger>
      <TooltipContent>Pré-rempli par l’IA{pct != null ? ` · confiance ${pct}%` : ''}</TooltipContent>
    </Tooltip>
  );
}

interface Props {
  code: string;
  model: EnMappingModel;
  decisions: EnMappingDecisions;
  /** Mise à jour fonctionnelle : indispensable car l'auto-match / IA applique
   *  plusieurs correspondances dans le même tick (sinon closure obsolète). */
  onChange: (updater: (prev: EnMappingDecisions) => EnMappingDecisions) => void;
}

/**
 * Correspondances « Éducation nationale » : associe niveaux et matières aux
 * nomenclatures officielles. Pré-mapping assisté par IA (Gemini) à l'entrée de
 * l'étape : l'IA propose (✨), l'opérateur corrige. Les codes (`code_niveau`,
 * `codemef`, `code_cycle`, `code_matiere`) sont injectés dans les CSV.
 */
export function EducationNationaleMapping({ code, model, decisions, onChange }: Props) {
  // null = inconnu (appel en cours), true/false = IA dispo ou non (clé configurée).
  const [aiEnabled, setAiEnabled] = useState<boolean | null>(null);
  const [aiNiveauxRunning, setAiNiveauxRunning] = useState(false);
  const aiNiveauxSigRef = useRef<string>('');

  const niveauOptions = useMemo<SearchSelectOption[]>(
    () => [
      { value: NONE, label: '(non mappé)' },
      ...model.options.niveaux.map((o) => ({
        value: o.code,
        label: `${o.label} (${o.code})`,
        searchText: o.code,
      })),
    ],
    [model.options.niveaux],
  );

  const searchMefs = useCallback((q: string) => searchEnMefs(code, q), [code]);

  const setNiveau = (value: string, patch: Partial<EnMappingDecisions['niveaux'][string]>) => {
    onChange((prev) => {
      const cur =
        prev.niveaux[value] ?? { niveauCode: null, codeCycle: null, mefCode: null, mefLabel: null };
      // Action utilisateur → la ligne devient « manuelle » (retire l'✨).
      const merged = { ...cur, ...patch, source: 'manual' as EnMappingSource, confidence: null };
      if ('niveauCode' in patch) {
        merged.codeCycle = cycleForNiveau(model.options.niveaux, merged.niveauCode);
      }
      return { ...prev, niveaux: { ...prev.niveaux, [value]: merged } };
    });
  };

  const setMatiere = (
    niveau: string,
    nom: string,
    matiereCode: string | null,
    matiereLabel: string | null,
    source?: EnMappingSource,
    confidence?: number | null,
  ) => {
    onChange((prev) => {
      const niveauMatieres = { ...(prev.matieres[niveau] ?? {}) };
      niveauMatieres[nom] = { matiereCode, matiereLabel, source, confidence: confidence ?? null };
      return { ...prev, matieres: { ...prev.matieres, [niveau]: niveauMatieres } };
    });
  };

  // Pré-mapping IA des niveaux (+ MEF) à l'entrée de l'étape, une fois par jeu de
  // libellés. Ne remplit que les valeurs absentes, jamais un choix manuel.
  useEffect(() => {
    const labels = model.niveaux.values.map((v) => v.value);
    if (labels.length === 0) return;
    const sig = labels.join('|');
    if (aiNiveauxSigRef.current === sig) return;
    aiNiveauxSigRef.current = sig;

    // NB : pas de flag `alive` qui jetterait le résultat — en StrictMode (dev)
    // le cleanup l'annulerait et la 2ᵉ invocation serait sautée par le ref, donc
    // rien ne s'appliquerait. Les suggestions ciblent l'état parent (stable).
    setAiNiveauxRunning(true);
    premapNiveauxAi(code, labels)
      .then((resp) => {
        setAiEnabled(resp.enabled);
        if (!resp.enabled || resp.suggestions.length === 0) return;
        onChange((prev) => {
          const niveaux = { ...prev.niveaux };
          for (const s of resp.suggestions) {
            const cur =
              niveaux[s.value] ?? { niveauCode: null, codeCycle: null, mefCode: null, mefLabel: null };
            if (cur.source === 'manual') continue;
            const niveauCode = cur.niveauCode ?? s.niveauCode ?? null;
            const mefCode = cur.mefCode ?? s.mefCode ?? null;
            const contributed =
              (cur.niveauCode == null && s.niveauCode) || (cur.mefCode == null && s.mefCode);
            if (!contributed) continue;
            niveaux[s.value] = {
              niveauCode,
              codeCycle: cycleForNiveau(model.options.niveaux, niveauCode),
              mefCode,
              mefLabel: cur.mefLabel ?? s.mefLabel ?? null,
              source: 'ai',
              confidence: s.confidence,
            };
          }
          return { ...prev, niveaux };
        });
      })
      .catch(() => setAiEnabled(false))
      .finally(() => setAiNiveauxRunning(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, model]);

  const matieresByNiveau = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const v of model.matieres.values) {
      if (!map.has(v.niveau)) map.set(v.niveau, []);
      map.get(v.niveau)!.push(v.nom);
    }
    return Array.from(map.entries());
  }, [model.matieres.values]);

  const showNiveaux = model.niveaux.present && model.niveaux.values.length > 0;
  const showMatieres = model.matieres.present && model.matieres.values.length > 0;

  if (!showNiveaux && !showMatieres) {
    return null;
  }

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-6">
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Cette école est de type <strong>Éducation nationale</strong>. Associez chaque niveau (→{' '}
            <code>code_niveau</code>, <code>codemef</code>, <code>code_cycle</code>) puis chaque
            matière (→ <code>code_matiere</code>). Les matières proposées sont{' '}
            <strong>celles associables au MEF du niveau</strong> ; les codes seront ajoutés
            automatiquement à vos fichiers. Les valeurs marquées{' '}
            <Sparkles className="inline h-3 w-3 text-violet-500 align-middle" /> ont été{' '}
            <strong>pré-remplies par l’IA</strong> — vérifiez-les et corrigez si besoin.
          </AlertDescription>
        </Alert>

        {(aiNiveauxRunning || aiEnabled === true) && (
          <div className="flex items-center gap-2 rounded-md border border-violet-200 bg-violet-50 px-3 py-2 text-sm text-violet-800 dark:border-violet-900 dark:bg-violet-950/40 dark:text-violet-200">
            {aiNiveauxRunning ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Pré-mapping IA en cours…
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 text-violet-500" />
                Champs marqués <Sparkles className="inline h-3 w-3 text-violet-500" /> pré-remplis par
                l’IA — vérifiez et corrigez si besoin.
              </>
            )}
          </div>
        )}

        {showNiveaux && (
          <Card>
            <CardHeader icon={<GraduationCap />} iconColor="blue">
              <CardTitle className="text-base">
                Niveaux{' '}
                <span className="font-normal text-muted-foreground">
                  — correspondance avec la nomenclature
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="bg-muted text-left">
                    <tr>
                      <th className="px-3 py-2 w-1/4">Niveau (fichier)</th>
                      <th className="px-3 py-2">Niveau EN → code_niveau</th>
                      <th className="px-3 py-2">MEF → codemef</th>
                      <th className="px-3 py-2 w-32">Cycle (auto)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {model.niveaux.values.map((v) => {
                      const d = decisions.niveaux[v.value] ?? {
                        niveauCode: null,
                        codeCycle: null,
                        mefCode: null,
                        mefLabel: null,
                      };
                      return (
                        <tr key={v.value} className="border-t align-middle">
                          <td className="px-3 py-2 font-medium">
                            {v.value}
                            {d.source === 'ai' && <AiMark confidence={d.confidence} />}
                          </td>
                          <td className="px-3 py-2">
                            <SearchSelect
                              options={niveauOptions}
                              value={d.niveauCode ?? ''}
                              onValueChange={(c) =>
                                setNiveau(v.value, { niveauCode: c === NONE ? null : c })
                              }
                              placeholder="Choisir un niveau…"
                              contentClassName="min-w-[20rem]"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <RemoteSearchSelect
                              value={d.mefCode ?? ''}
                              selectedLabel={d.mefLabel}
                              onSelect={(mefCode, mefLabel) =>
                                setNiveau(v.value, { mefCode: mefCode || null, mefLabel })
                              }
                              search={searchMefs}
                              placeholder="Choisir un MEF…"
                              searchPlaceholder="Rechercher un MEF (libellé ou code)…"
                              contentClassName="min-w-[24rem]"
                            />
                          </td>
                          <td className="px-3 py-2">
                            {d.codeCycle ? (
                              <Badge variant="secondary">{d.codeCycle}</Badge>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {showMatieres && (
          <Card>
            <CardHeader icon={<BookOpen />} iconColor="blue">
              <CardTitle className="text-base">
                Matières{' '}
                <span className="font-normal text-muted-foreground">
                  — contraintes par le MEF de chaque niveau
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {matieresByNiveau.map(([niveau, noms]) => (
                <NiveauMatieres
                  key={niveau || '__none__'}
                  code={code}
                  niveau={niveau}
                  mefCode={decisions.niveaux[niveau]?.mefCode ?? null}
                  noms={noms}
                  decisions={decisions.matieres[niveau] ?? {}}
                  aiEnabled={aiEnabled}
                  onSet={(nom, c, l, source, confidence) =>
                    setMatiere(niveau, nom, c, l, source, confidence)
                  }
                />
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </TooltipProvider>
  );
}

/**
 * Matières d'un niveau : charge les matières associables au MEF, pré-remplit par
 * auto-match exact (libellé normalisé) puis, pour le reste, par IA (✨).
 */
function NiveauMatieres({
  code,
  niveau,
  mefCode,
  noms,
  decisions,
  aiEnabled,
  onSet,
}: {
  code: string;
  niveau: string;
  mefCode: string | null;
  noms: string[];
  decisions: Record<
    string,
    { matiereCode: string | null; matiereLabel: string | null; source?: EnMappingSource; confidence?: number | null }
  >;
  aiEnabled: boolean | null;
  onSet: (
    nom: string,
    code: string | null,
    label: string | null,
    source?: EnMappingSource,
    confidence?: number | null,
  ) => void;
}) {
  const [options, setOptions] = useState<EnMatiereOption[]>([]);
  const [loading, setLoading] = useState(false);
  const aiMatSigRef = useRef<string>('');

  useEffect(() => {
    if (!mefCode) {
      setOptions([]);
      return;
    }
    let alive = true;
    setLoading(true);
    fetchMatieresForMef(code, mefCode)
      .then((items) => alive && setOptions(items))
      .catch(() => alive && setOptions([]))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [code, mefCode]);

  // Auto-match exact (déterministe) puis IA pour les libellés restants.
  useEffect(() => {
    if (options.length === 0) return;
    const byKey = new Map<string, EnMatiereOption>();
    for (const o of options) {
      const k = normalizeLabel(o.label);
      if (!byKey.has(k)) byKey.set(k, o);
    }
    const unmatched: string[] = [];
    for (const nom of noms) {
      if (decisions[nom]?.matiereCode) continue;
      const match = byKey.get(normalizeLabel(nom));
      if (match) onSet(nom, match.code, match.label); // exact → pas d'✨
      else unmatched.push(nom);
    }

    if (aiEnabled === true && mefCode && unmatched.length > 0) {
      const sig = `${mefCode}|${unmatched.join('|')}`;
      if (aiMatSigRef.current !== sig) {
        aiMatSigRef.current = sig;
        premapMatieresAi(code, mefCode, unmatched)
          .then((resp) => {
            if (!resp.enabled) return;
            for (const s of resp.suggestions) {
              if (s.matiereCode) onSet(s.nom, s.matiereCode, s.matiereLabel, 'ai', s.confidence);
            }
          })
          .catch(() => {});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, aiEnabled, mefCode]);

  const selectOptions = useMemo<SearchSelectOption[]>(
    () => [
      { value: NONE, label: '(non mappé)' },
      ...options.map((o) => {
        const lv = lvLabel(o);
        return {
          value: o.code,
          label: `${o.label} · ${modaliteText(o)}${lv ? ` · ${lv}` : ''} (${o.code})`,
          searchText: `${o.code} ${o.modalite ?? ''}${o.isLangueVivante ? ` langue vivante ${lv ?? ''}` : ''}`,
        };
      }),
    ],
    [options],
  );

  const optionByCode = useMemo(() => new Map(options.map((o) => [o.code, o])), [options]);

  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <span className="text-sm font-semibold">{niveau || 'Sans niveau'}</span>
        {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
        {mefCode && !loading && (
          <Badge variant="secondary" className="text-xs">
            {options.length} matières associables
          </Badge>
        )}
      </div>

      {!niveau ? (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Ces matières n’ont pas de niveau dans le fichier : ajoutez une colonne <code>niveau</code>{' '}
            pour les contraindre par MEF.
          </AlertDescription>
        </Alert>
      ) : !mefCode ? (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Choisissez d’abord le <strong>MEF</strong> du niveau « {niveau} » (table Niveaux
            ci-dessus) pour proposer les matières associables.
          </AlertDescription>
        </Alert>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left">
              <tr>
                <th className="px-3 py-2 w-1/4">Matière (fichier)</th>
                <th className="px-3 py-2">Matière EN → code_matiere</th>
                <th className="px-3 py-2 w-40">Modalité (imposée)</th>
              </tr>
            </thead>
            <tbody>
              {noms.map((nom) => {
                const d = decisions[nom] ?? { matiereCode: null, matiereLabel: null };
                const selected = d.matiereCode ? optionByCode.get(d.matiereCode) : undefined;
                return (
                  <tr key={nom} className="border-t align-middle">
                    <td className="px-3 py-2 font-medium">
                      {nom}
                      {d.source === 'ai' && <AiMark confidence={d.confidence} />}
                    </td>
                    <td className="px-3 py-2">
                      <SearchSelect
                        options={selectOptions}
                        value={d.matiereCode ?? ''}
                        onValueChange={(c) => {
                          if (c === NONE) return onSet(nom, null, null, 'manual');
                          const opt = optionByCode.get(c);
                          onSet(nom, c, opt?.label ?? null, 'manual');
                        }}
                        placeholder="Choisir une matière…"
                        contentClassName="min-w-[24rem]"
                      />
                    </td>
                    <td className="px-3 py-2">
                      {selected ? (
                        <div className="flex flex-wrap items-center gap-1">
                          <Badge variant="secondary" className="text-xs">
                            {modaliteText(selected)}
                          </Badge>
                          {selected.isLangueVivante && lvLabel(selected) && (
                            <Badge
                              variant="outline"
                              className="border-sky-300 bg-sky-50 text-xs text-sky-700 dark:border-sky-800 dark:bg-sky-950 dark:text-sky-300"
                            >
                              {lvLabel(selected)}
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
